<script setup lang="ts">
import VueDatePicker from "@vuepic/vue-datepicker";
import type { DatePickerInstance } from "@vuepic/vue-datepicker";
import { ref, computed, onMounted, nextTick, watch, ComputedRef } from "vue";
import {
  MaybeElement,
  onClickOutside as useClickOutside,
  createReusableTemplate,
} from "@vueuse/core";
import { DateTime } from "luxon";
import { useI18n } from "vue-i18n";
import useMobileBreakpoint from "../../composables/useMobileBreakpoint.ts";
import { VacvMobileOverlay, USER_GROUP } from "vacv-components";
import { DAYS_FROM_DEPARTURE, MAXIMUM_STAY } from "../../constants/index.ts";
import { DateRangeData } from "../../../types/form-data";
import {
  dataLayerSearchWidgetError,
  dataLayerSearchWidgetDateValidationMethod,
} from "../../analytics/package-search-form";
import { Locales, SearchType } from "../../../types/tabs";
import { FlightTrip } from "../../../types/common";
import { getEnvVar } from "../../utils/env";
import { storeToRefs } from "pinia";
import { useFlightAvailabilityStore } from "../../stores/flightAvailabilityStore";

const facalendar =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm64 80v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm128 0v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H208c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H336zM64 400v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H208zm112 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H336c-8.8 0-16 7.2-16 16z"/></svg><span class=\'sr-only\'>Date</span>';

export interface Props {
  minStay?: number;
  defaultDepartureDate: string;
  defaultReturnDate: string;
  tabId: string;
  searchType?: SearchType;
  audience?: USER_GROUP;
  minDate?: string;
  maxDate?: string;
  flightTrip: FlightTrip | null;
}
const props = withDefaults(defineProps<Props>(), {
  minStay: 3,
  searchType: SearchType.Package,
});

const emit =
  defineEmits<(e: "selectDates", travelDates: DateRangeData) => void>();
const { t, locale } = useI18n({ useScope: "global" });
const displayFormat = "dd/MM/yyyy";

const today = DateTime.now().toJSDate();

const desktopDatepicker = ref<DatePickerInstance>(null);
const mobileDatepicker = ref<DatePickerInstance>(null);
const datepickerComponent = ref<MaybeElement>(null);
const calendarDisplay = ref(false);

const departureDate = ref<string>(props.defaultDepartureDate);
const returnDate = ref<string>(props.defaultReturnDate);
const travelDates = ref<Date[]>();

type focusOptions = "departureDate" | "returnDate" | null;
const focusOn = ref<focusOptions>(null);
const mobileBreakPoint = 767;
const { isMobile } = useMobileBreakpoint(mobileBreakPoint);

const flightAvailabilityStore = useFlightAvailabilityStore();

const {
  getCellClassName,
  setFlightTrip,
  updateDisplayedMonths,
  onChangeMonthYear,
  reloadAvailabilityData,
} = flightAvailabilityStore;

const {
  VITE_FLIGHT_AVAILABILITY_URL,
  DEBOUNCE_TIMEOUT,
  FETCH_TIMEOUT,
  hasUnavailableFlights,
  minDate: minimumDate,
  maxDate: maximumDate,
} = storeToRefs(flightAvailabilityStore);

VITE_FLIGHT_AVAILABILITY_URL.value = getEnvVar("VITE_FLIGHT_AVAILABILITY_URL");
DEBOUNCE_TIMEOUT.value = 300;

minimumDate.value = props.minDate;
maximumDate.value = props.maxDate;

if (getEnvVar("VITE_FLIGHT_AVAILABILITY_TIMEOUT")) {
  FETCH_TIMEOUT.value = parseInt(getEnvVar("VITE_FLIGHT_AVAILABILITY_TIMEOUT"));
}

onMounted(() => {
  if (props.defaultDepartureDate && props.defaultReturnDate) {
    const defaultDepartureDate = DateTime.fromFormat(
      props.defaultDepartureDate,
      displayFormat
    ).toJSDate();
    const defaultReturnDate = DateTime.fromFormat(
      props.defaultReturnDate,
      displayFormat
    ).toJSDate();
    travelDates.value = [defaultDepartureDate, defaultReturnDate];
  }
});

useClickOutside(datepickerComponent, () => {
  if (calendarDisplay.value) {
    closeMenu();
    dataLayerSearchWidgetDateValidationMethod(
      "Click Out of Calendar Component"
    );
  }
});

const openMenu = (fieldName: focusOptions) => {
  calendarDisplay.value = true;
  if (
    fieldName === "departureDate" ||
    (fieldName === "returnDate" && travelDates.value?.length)
  ) {
    focusOn.value = fieldName;
  }
  nextTick(() => desktopDatepicker.value?.openMenu());

  removeDisableDateTabbing();
};

// For accessibility purpose:
// remove tabindex from disabled dates in the calendar
// https://aircanadavacations.atlassian.net/browse/HD-777
const removeDisableDateTabbing = () => {
  setTimeout(() => {
    document
      .querySelectorAll(".dp__cell_inner:not(.dp__pointer)")
      .forEach((item) => item.parentElement?.removeAttribute("tabindex"));
    document
      .querySelectorAll(".dp__month_year_select")
      .forEach((item) => item?.setAttribute("disabled", "true"));
  }, 10);
};

const datePickerIsOpen = ref(false);

const onOpenDatePicker = () => {
  const wasAlreadyOpen = datePickerIsOpen.value;

  if (wasAlreadyOpen) {
    // this will force a reload of the data when switching between departure and return text fields
    reloadAvailabilityData(isDepartureSelected.value);

    return;
  }

  datePickerIsOpen.value = true;

  let startDate;

  if (departureDate.value) {
    startDate = DateTime.fromFormat(departureDate.value, "dd/MM/yyyy").startOf(
      "month"
    );
  } else {
    startDate = DateTime.now().startOf("month");
  }
  const endDate = startDate.plus({ months: 1 }).endOf("month");

  updateDisplayedMonths(
    isDepartureSelected.value,
    startDate.get("year"),
    startDate.get("month") - 1, // zero indexed
    endDate.get("year"),
    endDate.get("month") - 1, // zero indexed
    false
  );
};

const closeMenu = () => {
  desktopDatepicker.value?.closeMenu();
  calendarDisplay.value = false;
  focusOn.value = null;

  datePickerIsOpen.value = false;
};

const conditionedClosing = () => {
  setTimeout(() => {
    if (!document.activeElement?.closest(".date-picker")) {
      closeMenu();
    }
  }, 0);
};

const isReturnSelected = computed(
  () =>
    focusOn.value === "returnDate" ||
    (travelDates.value && travelDates.value?.length === 1)
);

const isDepartureSelected = computed(() => !isReturnSelected.value);

// For accessibility purpose:
// Handle shiftTab behaviour which gets trapped within DP
const handleTabPress = (e: KeyboardEvent) => {
  if (e.shiftKey && document.activeElement!.className.includes("dp__menu")) {
    if (focusOn.value === "returnDate") {
      (document.querySelector(`#departureDateId`) as HTMLElement)?.focus();
    }
  }
};

const selectDates = () => {
  desktopDatepicker.value!.selectDate();
  closeMenu();
  dataLayerSearchWidgetDateValidationMethod(
    "Click on Select These Days Button"
  );
};

const clearCalendarDates = () => {
  departureDate.value = "";
  returnDate.value = "";
  travelDates.value = [];
  focusOn.value = "departureDate";

  setTimeout(() => {
    // sometimes the "Clear dates" can be clicked at a time which will cause the month to not be loaded properly
    // so always reload after clearing
    reloadAvailabilityData(isDepartureSelected.value);
  }, DEBOUNCE_TIMEOUT.value);
};

const rangeStart = (selectedDate: Date) => {
  const selectedDateToLuxon = DateTime.fromJSDate(selectedDate);
  if (focusOn.value === "returnDate") {
    rangeEnd(selectedDate);
  } else {
    clearCalendarDates();
    departureDate.value = selectedDateToLuxon.toFormat(displayFormat);
    travelDates.value = [selectedDate];
    focusOn.value = "returnDate";

    // departure date is chosen, we must now load the return flight availability datata
    reloadAvailabilityData(isDepartureSelected.value);
  }
};

// event triggered by the date-picker
const rangeEnd = (selectedDate: Date) => {
  const selectedDateToLuxon = DateTime.fromJSDate(selectedDate);
  const departureDateToLuxon = DateTime.fromFormat(
    departureDate.value,
    displayFormat
  );
  const interval = selectedDateToLuxon.diff(departureDateToLuxon, "days").days;
  if (isNaN(interval) || interval <= 0) {
    clearCalendarDates();
    departureDate.value = selectedDateToLuxon.toFormat(displayFormat);
    travelDates.value = [selectedDate];
    focusOn.value = "returnDate";
  } else {
    returnDate.value = selectedDateToLuxon.toFormat(displayFormat);
    travelDates.value = [departureDateToLuxon.toJSDate(), selectedDate];
    focusOn.value = "returnDate";
  }
};

const selectingDateTitle = computed(() => {
  return isReturnSelected.value
    ? t(`form.datepicker.${props.searchType}.returnDateSelectTitle`)
    : t(`form.datepicker.${props.searchType}.departureDateSelectTitle`);
});

const datesInfoCount = computed(() => {
  if (travelDates.value?.length === 2) {
    const [d, r] = travelDates.value;
    if (d && r) {
      return DateTime.fromJSDate(r).diff(DateTime.fromJSDate(d), "days").days;
    }
  }
  return null;
});

const checkDepartureError = (departureDate: Date, returnDate: Date | null) => {
  let error = null;
  const interval = DateTime.fromJSDate(departureDate).diff(
    DateTime.fromJSDate(today),
    "days"
  ).days;
  if (interval < DAYS_FROM_DEPARTURE) {
    error = "daysFromDeparture";
    if (props.audience === USER_GROUP.EMPLOYEE) {
      error =
        props.searchType === SearchType.Hotels
          ? "daysFromDepartureEmployeeHotel"
          : "daysFromDepartureEmployee";
    }
    !returnDate && dataLayerSearchWidgetError("Booking For Next 24 hrs");
  }
  return error;
};

const checkDepartureReturnError = (departureDate: Date, returnDate: Date) => {
  let error = null;
  const interval = DateTime.fromJSDate(returnDate).diff(
    DateTime.fromJSDate(departureDate),
    "days"
  ).days;
  if (interval < props.minStay) {
    error = "minimumStay";
  }
  if (interval > MAXIMUM_STAY) {
    error = "maximumStay";
    dataLayerSearchWidgetError("Travel Dates More Than 28 Days Apart");
  }
  return error;
};

const errorState: ComputedRef<string | null> = computed(() => {
  const [d = null, r = null] = travelDates.value ?? [];
  let errorDepart = null,
    errorDepartReturn = null;
  if (d) {
    errorDepart = checkDepartureError(d, r);
  }
  if (d && r) {
    errorDepartReturn = checkDepartureReturnError(d, r);
  }
  return errorDepart ?? errorDepartReturn;
});

const errorDetails: ComputedRef<string | null> = computed(() => {
  if (errorState.value === "minimumStay") {
    return t("form.datepicker.minimumStay", { min: props.minStay });
  } else {
    return errorState.value ? t(`form.datepicker.${errorState.value}`) : null;
  }
});

//DATE_MED_WITH_WEEKDAY
const datesInfoLabel = computed(() => {
  let infoLabel = "";
  const datesInfoFormat =
    locale.value === Locales.FR ? "ccc dd LLL" : "ccc, LLL dd";
  if (travelDates.value?.length === 2) {
    const [d, r] = travelDates.value;
    const departureFormatted = DateTime.fromJSDate(d).toFormat(
      datesInfoFormat,
      {
        locale: locale.value,
      }
    );
    const returnFormatted = DateTime.fromJSDate(r).toFormat(datesInfoFormat, {
      locale: locale.value,
    });
    infoLabel = t("form.datepicker.departureToReturn", {
      departure: departureFormatted,
      return: returnFormatted,
    });
  }
  return infoLabel;
});

const dayLabels = computed(() => {
  return [1, 2, 3, 4, 5, 6, 7].map((day) =>
    t(`form.datepicker.dayLabels.${day}`)
  );
});

const validDates = computed(() => {
  let valid = true;
  const [d, r] = travelDates.value ?? [];
  if (!d || !r) {
    valid = false;
  }
  if (errorState.value) {
    valid = false;
  }
  return valid;
});

watch(travelDates, (values) => {
  const [d = null, r = null] = values ?? [];
  const data: DateRangeData = {
    isValid: validDates.value,
  };
  if (d) {
    data.departureDate = DateTime.fromJSDate(d).toFormat(displayFormat);
  }
  if (r) {
    data.returnDate = DateTime.fromJSDate(r).toFormat(displayFormat);
  }

  emit("selectDates", data);
});

const onModalEnter = () => {
  nextTick(() => mobileDatepicker.value!.openMenu());
};

type TemplateVersion = "modal" | "in-form";

const defineRef = (
  el: DatePickerInstance,
  templateVersion: TemplateVersion
) => {
  if (templateVersion === "modal") {
    mobileDatepicker.value = el;
  }
  if (templateVersion === "in-form") {
    desktopDatepicker.value = el;
  }
};
const shouldDisplay = (templateVersion: TemplateVersion) => {
  return (
    (isMobile.value && templateVersion === "modal") ||
    (!isMobile.value && templateVersion === "in-form")
  );
};

const yyyy_mm_dd = "yyyy-MM-dd";

function getFlightDirection() {
  const origin = props.flightTrip?.origin ?? "";
  const destination = props.flightTrip?.destination ?? "";
  return isDepartureSelected.value
    ? origin + ":" + destination
    : destination + ":" + origin;
}

const getDayClass = (date: Date) => {
  if (!props.flightTrip) {
    return "";
  }

  const dateStr = DateTime.fromJSDate(date).toFormat(yyyy_mm_dd);
  const departDateStr = DateTime.fromFormat(
    departureDate.value,
    displayFormat
  ).toFormat(yyyy_mm_dd);
  const returnDateStr = DateTime.fromFormat(
    returnDate.value,
    displayFormat
  ).toFormat(yyyy_mm_dd);

  return getCellClassName(
    dateStr,
    departDateStr,
    returnDateStr,
    isDepartureSelected.value,
    getFlightDirection()
  );
};

watch(
  () => props.flightTrip,
  (flightTrip) => {
    setFlightTrip(flightTrip?.origin ?? "", flightTrip?.destination ?? "");
  },
  {
    immediate: true,
  }
);

function onUpdateMonthYear(data: {
  instance: number;
  month: number; // month is zero indexed (0 = january)
  year: number;
}) {
  removeDisableDateTabbing();

  if (props.flightTrip) {
    onChangeMonthYear(isDepartureSelected.value, data.month, data.year);
  }
}

const minDateObj = props.minDate
  ? DateTime.fromFormat(props.minDate, yyyy_mm_dd).toJSDate()
  : undefined;
const maxDateObj = props.maxDate
  ? DateTime.fromFormat(props.maxDate, yyyy_mm_dd).toJSDate()
  : undefined;

const [DefineTemplate, ReuseTemplate] = createReusableTemplate();
</script>

<template>
  <div class="date-picker" ref="datepickerComponent" id="datePicker">
    <DefineTemplate v-slot="{ templateVersion }">
      <div :class="{ mobile: templateVersion === 'modal' }">
        <div class="date-picker-trigger" :class="{ error: !validDates }">
          <FormKit
            type="text"
            :value="departureDate"
            v-model="departureDate"
            :placeholder="$t(`form.datepicker.${searchType}.departure`)"
            :name="'departureDate' + templateVersion"
            :prefix-icon="facalendar"
            readonly
            @blur="conditionedClosing()"
            :id="tabId + '-departureDateId'"
            autocomplete="off"
            :wrapper-class="{
              facalendar: true,
              focus: focusOn === 'departureDate',
            }"
            :key="departureDate + 'departureDate' + templateVersion"
            @focus="openMenu('departureDate')"
            validation-visibility="submit"
          />
          <FormKit
            type="text"
            :placeholder="$t(`form.datepicker.${searchType}.return`)"
            :name="'returnDate' + templateVersion"
            :value="returnDate"
            v-model="returnDate"
            readonly
            :id="tabId + '-returnDateId'"
            autocomplete="off"
            :wrapper-class="{
              noicon: true,
              focus: focusOn === 'returnDate',
            }"
            :key="returnDate + 'returnDate' + templateVersion"
            @focus="openMenu('returnDate')"
            validation-visibility="submit"
          />
        </div>

        <div
          class="vacv-form-error"
          role="alert"
          aria-atomic="true"
          v-if="templateVersion === 'in-form' && !validDates"
        >
          {{ $t("form.datepicker.datesInvalid") }}
        </div>
        <div
          class="date-picker-wrapper search-overlay"
          :style="{
            display:
              shouldDisplay(templateVersion) && calendarDisplay
                ? 'block'
                : 'none',
          }"
          role="dialog"
          id="dates-overlay"
        >
          <div class="title-wrapper">
            {{ selectingDateTitle }}
            <span
              v-if="errorState"
              class="travel-days-error"
              role="alert"
              aria-atomic="true"
            >
              {{ errorDetails }}
            </span>
          </div>
          <div class="date-picker-content">
            <VueDatePicker
              :ref="(el: DatePickerInstance) => defineRef(el, templateVersion)"
              class="calendar-content"
              :range="true"
              :locale="locale"
              :action-row="{ showSelect: false, showCancel: false }"
              :enable-time-picker="false"
              :multi-calendars="templateVersion === 'modal' ? false : 2"
              hide-offset-dates
              v-model="travelDates"
              :auto-position="false"
              :alt-position="() => ({ top: 0, left: 0 })"
              week-start="0"
              month-name-format="long"
              no-today
              six-weeks
              :day-names="dayLabels"
              prevent-min-max-navigation
              @range-start="rangeStart($event)"
              @range-end="rangeEnd($event)"
              @open="onOpenDatePicker"
              @closed="closeMenu"
              @update-month-year="onUpdateMonthYear"
              @keyup.tab="handleTabPress($event)"
              :min-date="minDateObj"
              :max-date="maxDateObj"
              :vertical="false"
              :config="{
                onClickOutside: () => false,
                tabOutClosesMenu: false,
                closeOnClearValue: false,
                closeOnScroll: false,
                closeOnAutoApply: false,
                allowPreventDefault: false,
              }"
              :day-class="getDayClass"
            >
              <template #action-preview="">
                <!-- remove initial place-holder for date selected -->
              </template>
              <template #day="{ day }">
                {{ day }}
              </template>
              <template #dp-input="{}">
                <!-- remove initial provided inputs -->
              </template>
            </VueDatePicker>
            <div class="no-flights-legend" v-if="hasUnavailableFlights">
              <div class="no-flights-legend-box"></div>
              {{ $t("form.datepicker.noAvailableFlights") }}
            </div>
            <div class="date-picker-footer">
              <div
                class="current-selection"
                v-if="datesInfoLabel"
                role="alert"
                aria-atomic="true"
              >
                {{ datesInfoLabel }}
                <span
                  v-if="datesInfoCount"
                  :class="{
                    'travel-days-error': !validDates,
                  }"
                >
                  {{
                    $t("form.datepicker.departureToReturnCount", {
                      count: datesInfoCount,
                    })
                  }}
                </span>
              </div>
              <div class="footer-links">
                <button
                  type="button"
                  class="clear-button dp__action_cancel"
                  tabindex="0"
                  @blur="conditionedClosing()"
                  @click="clearCalendarDates"
                >
                  <i class="fa-solid fa-circle-xmark" aria-hidden="true"></i
                  >{{ $t("form.datepicker.clearDates") }}
                </button>
                <button
                  data-testid="selectDatesSubmitButton"
                  type="button"
                  class="select-button vacv-btn-secondary"
                  tabindex="0"
                  @click="selectDates()"
                  @blur="conditionedClosing()"
                  :disabled="!validDates"
                  :key="validDates + 'key'"
                >
                  {{ $t("form.datepicker.selectButton") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DefineTemplate>
    <VacvMobileOverlay
      :on-display="isMobile && calendarDisplay"
      @close="calendarDisplay = false"
      @before-enter="onModalEnter"
    >
      <ReuseTemplate template-version="modal" />
    </VacvMobileOverlay>
    <ReuseTemplate template-version="in-form" />
  </div>
</template>

<style lang="scss">
@use "./datepicker.scss";
</style>
